import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Lock, User, LogIn } from "lucide-react";
import logoColor from "../assets/logo-color-blanco.svg";

// Definimos el esquema de validación sin usar zod directamente
const loginSchema = {
  username: {
    required: "El usuario es requerido",
  },
  password: {
    required: "La contraseña es requerida",
  },
};

type LoginForm = {
  username: string;
  password: string;
};

export default function AdminLogin() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const form = useForm<LoginForm>({
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: LoginForm) => {
      const response = await fetch("https://jlufgkgssnxumguvknhc.functions.supabase.co/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Error al iniciar sesión");
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Guardar el token en localStorage para usarlo en futuras peticiones
      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("adminUser", JSON.stringify(data.user));

      toast({
        title: "Inicio de sesión exitoso",
        description: "Bienvenido al panel de administración",
      });

      // Redirigimos y recargamos la página
      window.location.href = "/admin";
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Usuario o contraseña incorrectos",
        variant: "destructive",
      });
    },
  });

  async function onSubmit(data: LoginForm) {
    loginMutation.mutate(data);
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error("Error loading logo:", e);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-6">
            <img
              src={logoColor}
              alt="InnoTec Food"
              className="h-16 mx-auto"
              onError={handleImageError}
            />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Panel de administración</h1>
          <p className="text-gray-400">Accede para gestionar tu contenido</p>
        </div>

        <Card className="border border-white/10 bg-[#111] shadow-xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-white">Iniciar sesión</CardTitle>
            <CardDescription>Ingresa tus credenciales para acceder</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90 flex items-center gap-2 text-sm font-medium">
                        <User className="w-4 h-4 text-[#FCAA0D]" />
                        Usuario
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Nombre de usuario"
                          className="bg-black border-white/10 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50
                                    text-white placeholder:text-white/40 h-11"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90 flex items-center gap-2 text-sm font-medium">
                        <Lock className="w-4 h-4 text-[#FCAA0D]" />
                        Contraseña
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          {...field}
                          placeholder="Tu contraseña"
                          className="bg-black border-white/10 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50
                                    text-white placeholder:text-white/40 h-11"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full font-montserrat uppercase tracking-wide text-sm font-bold
                            bg-[#FCAA0D] hover:bg-[#FCAA0D]/90
                            text-black h-12 mt-4 flex items-center justify-center gap-2"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-black/20 border-t-black/80 rounded-full animate-spin" />
                      <span>Iniciando sesión...</span>
                    </div>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      <span>Iniciar sesión</span>
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <a href="/" className="text-sm text-gray-400 hover:text-white transition-colors">
            Volver a la página principal
          </a>
        </div>
      </div>
    </div>
  );
}
